<script setup lang="ts">
import { useDeckPlayerContext } from '../../composables/useDeckPlayerShell'
import { useDraggableButton } from '../../composables/useFloatingPanel'

/**
 * Floating force-skip button ('perlingo.skip-button'): one click advances exactly
 * one node, even past media and wait nodes. Draggable; spam clicking is allowed.
 */
const { t, showSkipButton, runner } = useDeckPlayerContext()
const {
  buttonRef: skipBtnRef,
  moved: skipBtnDragMoved,
  style: skipBtnStyle,
  startDrag: startSkipBtnDrag,
  consumeDragClick,
} = useDraggableButton()

// Only guards a single physical click double-firing (stays below 10 clicks/s).
let lastSkipAt = 0
const onSkipBtnClick = () => {
  if (consumeDragClick()) return // that click ended a drag
  const now = Date.now()
  if (now - lastSkipAt < 90) return
  lastSkipAt = now
  runner.skipToNextNode()
}
</script>

<template>
  <Teleport to="body">
    <button
      v-if="showSkipButton"
      ref="skipBtnRef"
      type="button"
      class="pfc-skipbtn-float"
      :class="{ 'pfc-skipbtn-float--dragging': skipBtnDragMoved }"
      :style="skipBtnStyle"
      :title="t('deckPlayer.host.skipButton')"
      @mousedown="startSkipBtnDrag"
      @click="onSkipBtnClick"
    >
      ⏭
    </button>
  </Teleport>
</template>

<style scoped>
/* ── Skipknopf floating button ────────────────────────────────────────────── */
.pfc-skipbtn-float {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 999999;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: #22c55e;
  color: #fff;
  font-size: 1.4rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Only the --dragging modifier (an actual drag in progress) switches this
     to "grabbing" — plain :active would fire on every mousedown, including a
     simple click, making a click look like the start of a drag. */
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  user-select: none;
  touch-action: none;
  padding: 0;
  pointer-events: auto;
}
.pfc-skipbtn-float:hover {
  background: #16a34a;
}
.pfc-skipbtn-float--dragging {
  cursor: grabbing;
}
</style>
